import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { fmtDag, mag, type Recht } from '@ep/core';
import { pasToe } from './lib/thema';
import { api, ApiFout } from './lib/api';
import { useIk } from './lib/data';
import { Toasts, toast } from './ui/toast';
import { Laden } from './ui/ui';
import { Login, Aanmelden } from './pages/Login';
import { Projecten } from './pages/Projecten';
import { ProjectDetail } from './pages/ProjectDetail';
import { ProjectRapport } from './pages/ProjectRapport';
import { Werkpakketten } from './pages/Werkpakketten';
import { Projectplanning } from './pages/Projectplanning';
import { Tijdlijn } from './pages/Tijdlijn';
import { Taken } from './pages/Taken';
import { TaakScherm } from './pages/TaakScherm';
import { Weekrooster } from './pages/Weekrooster';
import { Verlof } from './pages/Verlof';
import { Bezetting } from './pages/Bezetting';
import { ExpertiseMatrix } from './pages/ExpertiseMatrix';
import { Templates } from './pages/Templates';
import { Beheer } from './pages/Beheer';
import { Gebruikers } from './pages/Gebruikers';
import { OrganisatieKiezer } from './ui/OrganisatieKiezer';
import { InstellingenPagina } from './pages/Instellingen';
import { Terugmelden } from './pages/Terugmelden';
import { MijnTaken } from './pages/MijnTaken';
import { Platform } from './pages/Platform';
import { MijnAgenda } from './pages/MijnAgenda';
import { Koppelen } from './pages/Koppelen';
import { Agile } from './pages/Agile';
import { MijnUren } from './pages/MijnUren';
import { UrenGoedkeuren, UrenRapport } from './pages/Uren';
import { Shell } from './ui/Shell';
import { Start } from './pages/Start';
import { Abonnement } from './pages/Abonnement';
import { TeamsStart } from './pages/TeamsStart';

export function App() {
  const loc = useLocation();
  if (loc.pathname.startsWith('/platform'))
    return (
      <>
        <Platform />
        <Toasts />
      </>
    );
  // SPEC §24: start van de Teams-app
  if (loc.pathname === '/teams') return <TeamsStart />;
  if (loc.pathname.startsWith('/koppelen/'))
    return (
      <>
        <Routes>
          <Route path="/koppelen/:token" element={<Koppelen />} />
        </Routes>
        <Toasts />
      </>
    );
  if (loc.pathname.startsWith('/terugmelden/'))
    return (
      <>
        <Routes>
          <Route path="/terugmelden/:token" element={<Terugmelden />} />
        </Routes>
        <Toasts />
      </>
    );
  return (
    <>
      <Ingelogd />
      <PrintVoet />
      <Toasts />
    </>
  );
}

function Ingelogd() {
  const ik = useIk();
  const loc = useLocation();
  const qc = useQueryClient();
  // SPEC §12.3: bij 402 of een uitgezette module de licentie opnieuw ophalen
  useEffect(() => {
    const opnieuw = () => void qc.invalidateQueries({ queryKey: ['ik'] });
    window.addEventListener('ep-licentie', opnieuw);
    return () => window.removeEventListener('ep-licentie', opnieuw);
  }, [qc]);
  // SPEC §13: eigen kleuren van de organisatie
  const hs = ik.data?.organisatie?.huisstijl;
  useEffect(() => {
    pasToe(hs ? { primair: hs.primair, accent: hs.accent } : null);
  }, [hs?.primair, hs?.accent]);
  if (ik.isLoading) return <Laden />;
  if (ik.error) {
    if (loc.pathname === '/aanmelden') return <Aanmelden />;
    return <Login />;
  }
  const s = ik.data!;
  if (!s.rol) return <Slot naam={s.naam} email={s.email} organisaties={s.organisaties ?? 0} />;
  // SPEC §12.3: geblokkeerd → beheerder ziet het abonnement, de rest een melding
  if (s.licentie?.toegang === 'GEBLOKKEERD')
    return (
      <Shell>
        {mag(s.rol, 'beheer') ? (
          <Abonnement />
        ) : (
          <div className="pagina">
            <div className="paneel" style={{ maxWidth: 640 }}>
              <h2>Geen toegang</h2>
              <p>{s.licentie.melding}</p>
              <p className="mute">
                Neem contact op met de beheerder van {s.organisatie.naam}.{' '}
                {s.verwijderOp ? <>Zonder nieuw abonnement worden de gegevens op <b>{fmtDag(s.verwijderOp, 'jaar')}</b> verwijderd.</> : 'Je gegevens blijven bewaard.'}
              </p>
            </div>
          </div>
        )}
      </Shell>
    );
  const uren = s.licentie?.modules.includes('UREN') ?? true;
  const agile = s.licentie?.modules.includes('AGILE') ?? true;
  const M = (aan: boolean, el: ReactNode) => (aan ? el : <ModuleUit />);
  if (s.rol === 'ENGINEER')
    return (
      <Shell>
        <Routes>
          <Route path="/mijn-agenda" element={<MijnAgenda />} />
          <Route path="/mijn-uren" element={M(uren, <MijnUren />)} />
          <Route path="*" element={<MijnTaken />} />
        </Routes>
      </Shell>
    );
  const R = (recht: Recht, el: ReactNode) => (mag(s.rol, recht) ? el : <GeenRechten />);
  // SPEC §4.6: een taak (nieuw of bestaand) opent als popup boven het scherm waar je vandaan kwam
  const taakPad = /^\/taken\/[^/]+$/.test(loc.pathname);
  const bewaard = (loc.state as { achtergrond?: typeof loc } | null)?.achtergrond;
  const achtergrond = taakPad ? (bewaard ?? { ...loc, pathname: '/taken', search: '', state: null }) : undefined;
  return (
    <Shell>
      {taakPad && (
        <Routes>
          <Route path="/taken/nieuw" element={R('taken.aanmaken', <TaakScherm key={loc.key} />)} />
          <Route path="/taken/:id" element={R('taken.aanmaken', <TaakScherm key={loc.pathname} />)} />
        </Routes>
      )}
      <Routes location={achtergrond ?? loc}>
        <Route path="/" element={<Start />} />
        <Route path="/projecten" element={<Projecten />} />
        <Route path="/projecten/:id" element={<ProjectDetail />} />
        <Route path="/projecten/:id/werkpakketten" element={<Werkpakketten />} />
        <Route path="/projecten/:id/planning" element={<Projectplanning />} />
        <Route path="/projecten/:id/rapport" element={<ProjectRapport />} />
        <Route path="/projecten/:id/agile/:wpId" element={M(agile, <Agile />)} />
        <Route path="/tijdlijn" element={<Tijdlijn />} />
        <Route path="/taken" element={R('taken.lezen', <Taken />)} />
        <Route path="/weekrooster" element={R('rooster', <Weekrooster />)} />
        <Route path="/verlof" element={R('verlof', <Verlof />)} />
        <Route path="/bezetting" element={R('rooster', <Bezetting />)} />
        <Route path="/expertise" element={R('rooster', <ExpertiseMatrix />)} />
        <Route path="/templates" element={R('templates', <Templates />)} />
        <Route path="/beheer" element={R('beheer', <Beheer />)} />
        <Route path="/beheer/gebruikers" element={R('beheer', <Gebruikers />)} />
        <Route path="/beheer/instellingen" element={R('beheer', <InstellingenPagina />)} />
        <Route path="/beheer/audit" element={<Navigate to="/beheer/instellingen?tab=audit" replace />} />
        <Route path="/beheer/koppelingen" element={<NaarKoppelingen />} />
        <Route path="/mijn-taken" element={<MijnTaken />} />
        <Route path="/mijn-agenda" element={<MijnAgenda />} />
        <Route path="/mijn-uren" element={M(uren, <MijnUren />)} />
        <Route path="/uren/rapport" element={M(uren, R('uren.alle', <UrenRapport />))} />
        <Route path="/uren/goedkeuren" element={M(uren, R('uren.goedkeuren', <UrenGoedkeuren />))} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Shell>
  );
}

/** SPEC §13.2: onderaan elke afgedrukte pagina. */
function PrintVoet() {
  const s = useIk().data;
  if (!s?.organisatie) return null;
  const eigen = !!(s.organisatie.huisstijl?.logoVersie || s.organisatie.huisstijl?.primair);
  return <div className="print-voet">{eigen ? <>{s.organisatie.naam} · powered by <b>bsmartr</b></> : <><b>bsmartr</b> · {s.organisatie.naam}</>}</div>;
}

/** SPEC §12.1: module zit niet in het abonnement. */
function ModuleUit() {
  const s = useIk().data!;
  return (
    <div className="pagina">
      <div className="paneel" style={{ maxWidth: 640 }}>
        <h2>Niet in jullie abonnement</h2>
        <p>Deze functie hoort bij een module die niet in het abonnement van {s.organisatie.naam} zit.</p>
        {mag(s.rol, 'beheer') ? <Link className="knop primair" to="/beheer/instellingen?tab=abonnement">Abonnement bekijken</Link> : <p className="mute">Vraag je beheerder om de module aan te zetten.</p>}
      </div>
    </div>
  );
}

function Slot({ naam, email, organisaties }: { naam: string; email: string; organisaties: number }) {
  const [kies, setKies] = useState(false);
  return (
    <div className="midden">
      <div className="paneel login" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44 }}>🔒</div>
        <h2>Nog geen toegang</h2>
        <p className="mute">
          Hoi {naam}, je bent ingelogd als <b>{email}</b>, maar je staat nog niet in de gebruikerslijst van deze organisatie. Vraag de beheerder om je toe te voegen.
        </p>
        <div className="rij" style={{ justifyContent: 'center' }}>
          {/* SPEC §17: wel gebruiker bij een andere organisatie */}
          {organisaties > 0 && <button className="knop primair" onClick={() => setKies(true)}>Kies een organisatie ({organisaties})</button>}
          <button className="knop" onClick={() => api.post('/auth/uitloggen').then(() => (window.location.href = '/'))}>
            Uitloggen
          </button>
        </div>
      </div>
      {kies && <OrganisatieKiezer naInloggen onSluit={() => setKies(false)} />}
    </div>
  );
}

function GeenRechten() {
  return (
    <div className="pagina">
      <div className="paneel leeg">🔒 Daar heb je met jouw rol geen toegang toe.</div>
    </div>
  );
}

export { ApiFout };

/** Oude link (en terugkeer na Microsoft-toestemming): Outlook en processen staat nu onder Instellingen. */
function NaarKoppelingen() {
  const loc = useLocation();
  const q = new URLSearchParams(loc.search);
  q.set('tab', 'koppelingen');
  return <Navigate to={`/beheer/instellingen?${q.toString()}`} replace />;
}
